import { Data as ArticleData } from '@root/modules/article/types/article';
import { WithContext, NewsArticle } from 'schema-dts';
import { Config } from '@root/modules/channel/types/channel';
import { articleUrl, articleCommentsUrl } from '@root/common/filters/urlBuilders';
import { convertToLocal } from '@root/common/filters/dateFormater';
import { stripTags, truncate } from '@root/common/filters/htmlFormater';
import { getCustomCropsPerRatio, getFocuspointQueryString } from '@root/libs/api-provider/utils/pictureCustomCrops';
import { getEnv } from '@root/common/utils/getEnv';

function articleJsonld(baseMeta: Config['meta']['base'], article: ArticleData) {
  let url, commentsUrl: string | undefined;

  try {
    url = new URL(articleUrl({ article }), baseMeta.baseUrl).href;
  } catch {
    url = `${baseMeta.baseUrl}${articleUrl({ article })}`;
  }

  try {
    commentsUrl = new URL(articleCommentsUrl({ article }), baseMeta.baseUrl).href;
  } catch {
    commentsUrl = `${baseMeta.baseUrl}${articleCommentsUrl({ article })}`;
  }

  const articleLd: WithContext<NewsArticle> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: article.content.title.text ? truncate(article.content.title.text, 100) : '',
    description: article.content.lead?.html ? stripTags(article.content.lead.html) : '',
    articleSection: article.primaryCategory?.name || '',
    commentCount: article.statistics.comments,
    discussionUrl: commentsUrl,
    datePublished: convertToLocal(article.publishAt),
    dateModified: convertToLocal(article.updatedAt),
    keywords: article.tags?.items.map((tag) => tag.name) || '',
    author: {
      '@type': 'Person',
      name: article.authors?.items.map((author) => author.name).join(',') || baseMeta.title,
    },
    publisher: {
      '@type': 'Organization',
      name: baseMeta.title,
      logo: {
        '@type': 'ImageObject',
        url: baseMeta.imageData.src,
      },
    },
  };

  if (article.metaImage) {
    const picture = article.metaImage;
    const customCrops = picture?.attributes?.cropperData?.customCrops;
    const customCropsPerRatio = getCustomCropsPerRatio(customCrops);
    const focuspoint = picture?.attributes?.cropperData?.focuspoint;
    const focusPointString = getFocuspointQueryString(focuspoint);
    const url = getEnv('mediaApiCropper');
    articleLd.image = `${url}/${picture.id}.jpg?w=1200&h=800${focusPointString}${customCropsPerRatio['16:9']}`;
  }

  return articleLd;
}

export default articleJsonld;
